import { useQuery } from '@tanstack/react-query';
import {
  loadViewDataQueryFn,
  type ViewDataParams,
} from 'shared/components/CustomTable/hooks/loadViewData';
import {
  type ApiError,
  addResourceNameToQueryResult,
  useLegacyAxiosInstance,
} from 'shared/hooks/utils';

export type GetPostponableInvoicesViewParams = ViewDataParams & { view_id: string };

export type PostponableInvoicesView = Awaited<ReturnType<typeof loadViewDataQueryFn>>;

// Per-resource wrapper over the generic views endpoint (loadViewDataQueryFn).
// Replaces the redux-saga `getView` read for the postponable-invoices (imminent
// reminders) list. Forward-compatible: when the backend exposes a dedicated list
// endpoint, only the queryFn here changes — the consumer keeps the same contract.
const emptyPostponableInvoices: PostponableInvoicesView = { data: [], metadata: undefined };

export function useGetPostponableInvoicesView(params: GetPostponableInvoicesViewParams) {
  const axiosInstance = useLegacyAxiosInstance();
  const { view_id, ...filters } = params;

  const queryResult = useQuery<PostponableInvoicesView, ApiError>({
    queryKey: ['postponable-invoices-view', params],
    queryFn: () => loadViewDataQueryFn(axiosInstance, view_id, filters),
    enabled: Boolean(view_id),
    placeholderData: previousData => previousData ?? emptyPostponableInvoices,
  });

  return addResourceNameToQueryResult<
    PostponableInvoicesView,
    ApiError,
    'postponableInvoices',
    'PreventUndefined'
  >('postponableInvoices', queryResult, emptyPostponableInvoices);
}
